import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { App as AntdApp } from 'antd';
import { useAuth, useAuthActions } from './hooks/useAuthStore';
import { ThemeProvider } from './components/theme/ThemeProvider';
import './styles/theme.css';

// Components
import LoginForm from './components/auth/LoginForm';
import ProtectedRoute from './components/auth/ProtectedRoute';
import DashboardLayout from './components/layout/DashboardLayout';

// Pages (will be created)
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import EmergencyPage from './pages/EmergencyPage';
import OTPPage from './pages/OTPPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ProfilePage from './pages/ProfilePage';

// AI Pages
import RadarAIDashboard from './pages/ai/RadarAIDashboard';

// AI Training Components
import { 
  RealTimeLearningDashboard, 
  InternetLearningMonitor, 
  ScheduledTrainingInterface 
} from './components/training';

// Safety Intelligence
import SafetyIntelligenceDashboard from './pages/SafetyIntelligenceDashboard';
import NotificationsPage from './pages/NotificationsPage';
import BeeNetworkPage from './pages/BeeNetworkPage';
import MessagingPage from './pages/MessagingPage';

const LoginWrapper: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  console.log('🔍 LoginWrapper - isAuthenticated:', isAuthenticated);

  if (isAuthenticated) {
    console.log('🎯 User is authenticated, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  return <LoginForm onSuccess={() => {
    console.log('🎯 Login success callback called, navigating to dashboard');
    navigate('/dashboard');
  }} />;
};

const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { checkAuth } = useAuthActions();

  // Check authentication on app start (only once)
  useEffect(() => {
    checkAuth();
  }, []); // Empty dependency array - only run once on mount

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* Login Route */}
        <Route 
          path="/login" 
          element={<LoginWrapper />}
        />

        {/* Protected Dashboard Routes */}
        <Route 
          path="/*" 
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  {/* Dashboard */}
                  <Route path="/dashboard" element={<DashboardPage />} />
                  
                  {/* Users Management */}
                  <Route path="/users" element={<UsersPage />} />
                  
                  {/* Emergency Management */}
                  <Route path="/emergency" element={<EmergencyPage />} />
                  <Route path="/emergency/beenetwork" element={<EmergencyPage />} />
                  <Route path="/emergency/radar-ai" element={<RadarAIDashboard />} />
                  <Route path="/emergency/support" element={<EmergencyPage />} />
                  <Route path="/emergency/technical" element={<EmergencyPage />} />
                  <Route path="/emergency/map" element={<EmergencyPage />} />
                  
                  {/* OTP Monitoring */}
                  <Route path="/otp" element={<OTPPage />} />
                  
                  {/* Messaging */}
                  <Route path="/messaging" element={<MessagingPage />} />
                  
                  {/* Analytics */}
                  <Route path="/analytics" element={<AnalyticsPage />} />
                  
                  {/* AI Systems */}
                  <Route path="/ai/radar" element={<RadarAIDashboard />} />
                  <Route path="/ai/patterns" element={<RealTimeLearningDashboard />} />
                  <Route path="/ai/anomaly" element={<InternetLearningMonitor />} />
                  <Route path="/ai/models" element={<ScheduledTrainingInterface />} />
                  
                                    {/* Safety Intelligence */}
                  <Route path="/safety-intelligence" element={<SafetyIntelligenceDashboard />} />

                  {/* Notifications */}
                  <Route path="/notifications" element={<NotificationsPage />} />

                  {/* BeeNetwork & Messaging */}
                  <Route path="/beenetwork" element={<BeeNetworkPage />} />
                  <Route path="/messaging" element={<MessagingPage />} />

                  {/* Profile */}
                  <Route path="/profile" element={<ProfilePage />} />
                  
                  {/* Default redirect */}
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  
                  {/* 404 */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          } 
        />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AntdApp>
        <AppContent />
      </AntdApp>
    </ThemeProvider>
  );
};

export default App;
